/**
 * T355 — FR-007a IMAP status banner.
 *
 * Renders above the expense list whenever the IMAP sync is in a non-ok
 * state (`retrying` or `failed`). Stays silent when the backend reports
 * `ok` or `never_ran` (no config → no banner). Polls every 30 s via
 * `useImapStatus`.
 */
import { useImapStatus } from '../../services/api/integrations';
import { formatDateTimeBerlin } from '../../lib/format-date';

function formatRelativeDe(ts: string | null): string {
  if (!ts) return '—';
  return formatDateTimeBerlin(ts) || ts;
}

export function ImapStatusBanner() {
  const { data } = useImapStatus();
  if (!data) return null;
  if (data.currentStatus === 'ok' || data.currentStatus === 'never_ran') {
    return null;
  }

  const isFailed = data.currentStatus === 'failed';
  const tone = isFailed
    ? 'bg-red-500/10 border-red-500/40 text-red-200'
    : 'bg-amber-500/10 border-amber-500/40 text-amber-200';

  return (
    <div
      className={`rounded-lg border px-4 py-3 text-sm ${tone}`}
      role="status"
      aria-live="polite"
      data-testid="imap-status-banner"
    >
      <div className="flex items-start gap-3">
        <span className="text-xl leading-none" aria-hidden="true">
          {isFailed ? '⛔' : '⏳'}
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-medium">
            {isFailed
              ? 'IMAP-Sync fehlgeschlagen'
              : `IMAP-Sync im Retry (Versuch ${data.consecutiveFailures})`}
          </div>
          {data.lastErrorMessage && (
            <div className="text-xs mt-1 opacity-80 break-words">
              {data.lastErrorMessage}
            </div>
          )}
          <div className="text-xs mt-1 opacity-70">
            Letzter Versuch: {formatRelativeDe(data.lastAttemptAt)} · Nächster Versuch:{' '}
            {formatRelativeDe(data.nextRetryAt)}
          </div>
        </div>
      </div>
    </div>
  );
}
